import { makeAutoObservable } from 'mobx'
import { Room } from 'livekit-client'
import { logger } from 'shared/lib'
import { toastStore } from 'shared/ui'
import { LINK_ENABLE_MICROPHONE } from 'shared/constants/links'
import { DeviceItemsEnum, DeviceSettingsStore } from 'entities/DeviceSettings'
import { AiCallingApi } from 'entities/AICalling/api/aiCallingApi'
import type { IResponseGettestSession } from 'entities/AICalling/api/types'

export class TestAgentStore {
  readonly deviceSettingsStore
  constructor(testAgentId?: number) {
    this.deviceSettingsStore = new DeviceSettingsStore({
      items: [DeviceItemsEnum.audioOutput, DeviceItemsEnum.audioInput],
    })
    if (testAgentId) {
      this.startTest(testAgentId)
    }
    makeAutoObservable(this)
  }
  private _data: IResponseGettestSession | null = null
  get data() {
    return this._data
  }
  private _setData = (data: typeof this._data) => {
    this._data = data
  }

  private _room: Room | null = null
  get room() {
    return this._room
  }
  private _createRoom = () => {
    this._room = new Room({
      audioCaptureDefaults: {
        deviceId: this.deviceSettingsStore.device.audioInput?.deviceId,
      },
      audioOutput: {
        deviceId: this.deviceSettingsStore.device.audioOutput?.deviceId,
      },
    })
      .on('connectionStateChanged', (state) => {
        if (this.loading) {
          if (state === 'disconnected') {
            this._reset()
            this.setLoading(false)
            toastStore.add({
              type: 'error',
              title: 'Something went wrong!',
            })
          }
          if (state === 'connected') {
            this.setLoading(false)
          }
        }
      })
      .on('mediaDevicesError', () => {
        this.setLoading(false)
      })
  }

  _loading = false
  get loading() {
    return this._loading
  }
  setLoading = (value: boolean) => {
    this._loading = value
  }

  startTest = async (id: number) => {
    try {
      const isAllowed = await this.checkPermissions()
      if (!isAllowed) {
        return
      }
      this.setLoading(true)

      const { data } = await AiCallingApi.getTestSession(id)
      this._setData(data)
      this._createRoom()
    } catch (e) {
      logger.error(e)
      this.setLoading(false)
    }
  }

  endTest = () => {
    this.room?.disconnect(true)
    this._reset()
  }

  private _reset = () => {
    this._data = null
    this._room = null
  }

  get isActiveAgent() {
    return this.room && !this.loading
  }

  checkPermissions = async () => {
    const isAllowed = await this.deviceSettingsStore.checkPermissions(true)
    this.deviceSettingsStore.removeStream()
    if (!isAllowed) {
      toastStore.add({
        type: 'error',
        title: 'Microphone access required',
        desc: 'To enable recording, please allow Salesmsg to access your microphone',
        action: {
          link: LINK_ENABLE_MICROPHONE,
          text: 'Learn more',
        },
      })
    }
    return isAllowed
  }
}
